import React, { useEffect, useState, useMemo, useContext } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Button,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  TableSortLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Chip,
} from '@mui/material';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { styled } from '@mui/material/styles';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { collection, query, onSnapshot, deleteDoc, doc, addDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import ConfirmationDialog from './ConfirmationDialog';
import InfoModalV2 from '../components/InfoModalV2';
import { createMediaSchema } from '../modals/MediaSchema';
import useIsMobile from '../utils/useIsMobile';
import { AppContext } from '../AppContext';
import AddMedium from './AddMedium';

function Media({ setError, setSuccess }) {
  const { user, userData } = useContext(AppContext);
  const activeFarmId = userData?.activeFarmId;
  const isMobile = useIsMobile();
  const [media, setMedia] = useState([]);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [mediumToEdit, setMediumToEdit] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedMedium, setSelectedMedium] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMedium, setConfirmMedium] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // New: InfoModal state
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [selectedMediumData, setSelectedMediumData] = useState(null);

  const [mediaSortColumn, setMediaSortColumn] = useState('brand');
  const [mediaSortDirection, setMediaSortDirection] = useState('asc');

  useEffect(() => {
    if (!user || !activeFarmId) {
      setMedia([]);
      return;
    }
    const mediaRef = collection(db, 'farms', activeFarmId, 'media');
    const q = query(mediaRef);
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setMedia(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });
    return unsubscribe;
  }, [user, activeFarmId]);

  const filteredMedia = useMemo(() => {
    return media.filter((medium) =>
      medium.brand.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [media, searchTerm]);

  const getPacketWeightString = (medium) => {
    if (!medium.packetWeight) return 'N/A';
    return `${medium.packetWeight}${medium.weightUnit || ''}`.trim();
  };

  const getPacketCostString = (medium) => {
    if (!medium.packetCost || isNaN(parseFloat(medium.packetCost))) return 'N/A';
    return medium.costCurrency ? `${medium.costCurrency}${medium.packetCost}` : medium.packetCost;
  };

  const sortedMedia = useMemo(() => {
    const sorted = [...filteredMedia].sort((a, b) => {
      let aValue, bValue;
      switch (mediaSortColumn) {
        case 'brand':
          aValue = a.brand ? a.brand.toLowerCase() : '';
          bValue = b.brand ? b.brand.toLowerCase() : '';
          break;
        case 'packetWeight':
          aValue = parseFloat(a.packetWeight) || 0;
          bValue = parseFloat(b.packetWeight) || 0;
          break;
        case 'packetCost':
          aValue = parseFloat(a.packetCost) || 0;
          bValue = parseFloat(b.packetCost) || 0;
          break;
        default:
          aValue = '';
          bValue = '';
      }
      if (aValue < bValue) return mediaSortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return mediaSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredMedia, mediaSortColumn, mediaSortDirection]);

  const paginatedMedia = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return sortedMedia.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedMedia, page, rowsPerPage]);

  const handleMediaSort = (column) => {
    if (mediaSortColumn === column) {
      setMediaSortDirection(mediaSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setMediaSortColumn(column);
      setMediaSortDirection('asc');
    }
  };

  const handleOpen = () => setOpen(true);
  const handleCloseDialog = () => {
    setOpen(false);
    setEditMode(false);
    setMediumToEdit(null);
  };
  const handleAddMedium = () => {
    handleCloseDialog();
  };

  const handleClone = async () => {
    if (!selectedMedium || !user) return;
    if (!activeFarmId) {
      setError?.('No active farm selected.');
      return;
    }
    try {
      const { id, ...dataToClone } = selectedMedium;
      await addDoc(collection(db, 'farms', activeFarmId, 'media'), dataToClone);
      setSuccess?.('Medium cloned successfully')
    } catch (err) {
      setError?.('Failed to clone medium.');
    }
    handleMenuClose();
  };

  const handleDelete = async (id) => {
    if (!user) return;
    if (!activeFarmId) {
      setError?.('No active farm selected.');
      return;
    }
    try {
      await deleteDoc(doc(db, 'farms', activeFarmId, 'media', id));
      setSuccess?.('Medium deleted successfully');
    } catch (error) {
      console.error('Error deleting medium:', error);
      setError?.('Failed to delete medium');
    }
    handleMenuClose();
  };

  const openConfirmDialog = (medium) => {
    setConfirmMedium(medium);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (confirmMedium) {
      await handleDelete(confirmMedium.id);
    }
    setConfirmOpen(false);
    setConfirmMedium(null);
  };

  const handleConfirmCancel = () => {
    setConfirmOpen(false);
    setConfirmMedium(null);
  };

  const handleMenuOpen = (event, medium) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedMedium(medium);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedMedium(null);
  };

  const handleEdit = () => {
    setEditMode(true);
    setMediumToEdit(selectedMedium);
    handleOpen();
    handleMenuClose();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // NEW: Modal open on row click
  const handleRowClick = (medium) => {
    setSelectedMediumData(medium);
    setInfoModalOpen(true);
  };

  const mediaSchema = useMemo(
    () => createMediaSchema({ getPacketWeightString, getPacketCostString }),
    [getPacketWeightString, getPacketCostString]
  );

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
    '& .MuiTableSortLabel-root': {
      color: `${theme.palette.common.white} !important`,
      '&.Mui-active': {
        color: `${theme.palette.common.white} !important`,
      },
      '& .MuiTableSortLabel-icon': {
        color: `${theme.palette.common.white} !important`,
      },
      '&:hover': {
        color: 'rgba(255, 255, 255, 0.75) !important',
      },
    },
  }));

  const subscriptionActive = isSubscriptionActive(userData);

  return (
    <Box
      sx={{
        backgroundColor: (theme) => theme.palette.background.default,
        color: (theme) => theme.palette.text.primary,
        display: 'flex',
        flexDirection: 'column',
        padding: '20px',
        width: '100%',
        maxWidth: '80%',
        margin: '0 auto',
        '@media (max-width: 600px)': { maxWidth: '100%' },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: (theme) => theme.palette.background.default,
          zIndex: -1,
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>Media</Typography>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleOpen}
          disabled={!subscriptionActive}
        >
          Add Medium
        </Button>
      </Box>
      {isMobile && (
        <Paper sx={{ mb: 2 }}>
          <List disablePadding>
            {paginatedMedia.map((medium) => (
              <React.Fragment key={medium.id}>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => handleRowClick(medium)} sx={{ alignItems: 'flex-start' }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle2">{medium.brand || 'N/A'}</Typography>
                          <Chip size="small" color="info" label={getPacketWeightString(medium)} />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" sx={{ display: 'block' }}>
                            {getPacketCostString(medium)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
            {paginatedMedia.length === 0 && (
              <ListItem>
                <ListItemText primary="No media found." />
              </ListItem>
            )}
          </List>
        </Paper>
      )}
      <TableContainer component={Paper} sx={{ display: { xs: 'none', sm: 'block' } }}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell>
                <TableSortLabel
                  active={mediaSortColumn === 'brand'}
                  direction={mediaSortColumn === 'brand' ? mediaSortDirection : 'asc'}
                  onClick={() => handleMediaSort('brand')}
                >
                  Name
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <TableSortLabel
                  active={mediaSortColumn === 'packetCost'}
                  direction={mediaSortColumn === 'packetCost' ? mediaSortDirection : 'asc'}
                  onClick={() => handleMediaSort('packetCost')}
                >
                  Packet Cost
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <TableSortLabel
                  active={mediaSortColumn === 'packetWeight'}
                  direction={mediaSortColumn === 'packetWeight' ? mediaSortDirection : 'asc'}
                  onClick={() => handleMediaSort('packetWeight')}
                >
                  Packet Weight
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>Notes</span>
                  <Tooltip
                    title={
                      <Typography sx={{ fontSize: '0.8rem' }}>
                        Mouse over note to read entire entry.
                      </Typography>
                    }
                  >
                    <HelpOutlineIcon fontSize="small" sx={{ cursor: 'pointer' }} />
                  </Tooltip>
                </Box>
              </StyledTableCell>
              <StyledTableCell>Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedMedia.map((medium) => (
              <TableRow key={medium.id} onClick={() => handleRowClick(medium)} sx={{ cursor: 'pointer' }}>
                <TableCell>{medium.brand || 'N/A'}</TableCell>
                <TableCell>{getPacketCostString(medium)}</TableCell>
                <TableCell>{getPacketWeightString(medium)}</TableCell>
                <TableCell
                  sx={{
                    maxWidth: 100,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Tooltip title={<Typography sx={{ fontSize: '0.8rem' }}>{medium.notes || ''}</Typography>}>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        width: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {medium.notes || ''}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <IconButton onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, medium); }}>
                    <SettingsIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {paginatedMedia.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No media found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filteredMedia.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
        <MenuItem disabled={!subscriptionActive} onClick={handleEdit}>Edit</MenuItem>
        <MenuItem disabled={!subscriptionActive} onClick={handleClone}>Clone</MenuItem>
        <MenuItem onClick={() => { handleMenuClose(); openConfirmDialog(selectedMedium); }}>
          Delete
        </MenuItem>
      </Menu>
      <AddMedium
        open={open}
        onClose={handleCloseDialog}
        onAdd={handleAddMedium}
        editMode={editMode}
        mediumToEdit={mediumToEdit}
        setSuccess={setSuccess}
        setError={setError}
      />

      <ConfirmationDialog
        open={confirmOpen}
        title="Confirm Medium Deletion"
        message="Are you sure you want to delete this medium?"
        onConfirm={handleConfirmDelete}
        onClose={handleConfirmCancel}
      />
      <InfoModalV2
        open={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
        data={selectedMediumData}
        schema={mediaSchema}
      />
    </Box>
  );
}

export default Media;

function isSubscriptionActive(userData) {
  if (!userData || !userData.accessExpires) return true;
  const now = new Date();
  const expires = userData.accessExpires.toDate
    ? userData.accessExpires.toDate()
    : new Date(userData.accessExpires);
  return now <= expires;
}






